import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { useToggle } from 'shared/hooks';
import { ProductRecommendationLookup, TempExpense, TempOrderNote, TempProduct, TTempCustomProduct } from 'shared/types';

interface QuoteDetailContext {
  newProduct: TempProduct;
  newExpense: TempExpense;
  newCustomProduct: TTempCustomProduct;
  newOrderNote: TempOrderNote;
  editMode: boolean;
  newProductDialogOpen: boolean;
  newProductByRecommendationDialogOpen: boolean;
  newCustomProductDialogOpen: boolean;
  newExpenseDialogOpen: boolean;
  newOrderNoteDialogOpen: boolean;
  reminderDialogOpen: boolean;
  reminderAllowed: boolean;
  recommendationsSelected: ProductRecommendationLookup[];
  pdfDialogOpen: boolean;
  confirmationOpen: boolean;
  toggleEditMode: () => void;
  toggleNewProductDialog: () => void;
  toggleNewProductByRecommendationDialog: () => void;
  toggleNewCustomProductDialog: () => void;
  toggleNewExpenseDialog: () => void;
  toggleNewOrderNoteDialog: () => void;
  togglePdfDialog: () => void;
  toggleConfirmation: () => void;
  toggleReminderDialogOpen: () => void;
  setReminderAllowed: (reminderAllowed: boolean) => void;
  setRecommendationsSelected: (recommendationsSelected: React.SetStateAction<ProductRecommendationLookup[]>) => void;
  setTempProduct: (product: TempProduct) => void;
  setTempCustomProduct: (customProduct: TTempCustomProduct) => void;
  setTempExpense: (expense: TempExpense) => void;
  setTempOrderNote: (orderNote: TempOrderNote) => void;
}

const quoteDetailContext = createContext({} as QuoteDetailContext);

export function ProvideQuoteState({ children }: PropsWithChildren<{}>) {
  const [editMode, toggleEditMode] = useToggle();
  const [newProductDialogOpen, toggleNewProductDialog] = useToggle();
  const [newProductByRecommendationDialogOpen, toggleNewProductByRecommendationDialog] = useToggle();
  const [newCustomProductDialogOpen, toggleNewCustomProductDialog] = useToggle();
  const [newExpenseDialogOpen, toggleNewExpenseDialog] = useToggle();
  const [newOrderNoteDialogOpen, toggleNewOrderNoteDialog] = useToggle();
  const [confirmationOpen, toggleConfirmation] = useToggle();
  const [pdfDialogOpen, togglePdfDialog] = useToggle();
  const [reminderDialogOpen, toggleReminderDialogOpen] = useToggle();
  const [newProduct, setTempProduct] = useState<TempProduct>(null);
  const [newCustomProduct, setTempCustomProduct] = useState<TTempCustomProduct>(null);
  const [newExpense, setTempExpense] = useState<TempExpense>(null);
  const [newOrderNote, setTempOrderNote] = useState<TempOrderNote>(null);
  const [reminderAllowed, setReminderAllowed] = useState(false);
  const [recommendationsSelected, setRecommendationsSelected] = useState<ProductRecommendationLookup[]>([]);

  return (
    <quoteDetailContext.Provider
      value={{
        newProduct,
        newCustomProduct,
        newExpense,
        newOrderNote,
        editMode,
        newProductDialogOpen,
        newProductByRecommendationDialogOpen,
        newCustomProductDialogOpen,
        newExpenseDialogOpen,
        newOrderNoteDialogOpen,
        pdfDialogOpen,
        reminderDialogOpen,
        confirmationOpen,
        reminderAllowed,
        recommendationsSelected,
        setTempProduct,
        setTempCustomProduct,
        setTempExpense,
        setTempOrderNote,
        setReminderAllowed,
        setRecommendationsSelected,
        toggleEditMode,
        toggleNewProductDialog,
        toggleNewProductByRecommendationDialog,
        toggleNewCustomProductDialog,
        togglePdfDialog,
        toggleNewOrderNoteDialog,
        toggleConfirmation,
        toggleNewExpenseDialog,
        toggleReminderDialogOpen,
      }}
    >
      {children}
    </quoteDetailContext.Provider>
  );
}

export function useQuoteState() {
  return useContext(quoteDetailContext);
}
